import { config } from "@/config/rainbow.config";
import { BLESSNET_MOD_PERMS_ADDRESS } from "@/constants/mod-perms-tool.constants";
import { BLESSNET_MOD_PERMS_ABI } from "@/config/abis/mod-perms-abi";
import { useUIStore } from "@/stores/ui-states.store";
import {
  ModifiedPermissionsRequest,
  type RemoveSingleDelegateType,
  type SocialAccountCreationRequest,
} from "@/types/modular-permissions.types";
import { HexString } from "@/types/general.types";
import { ethers } from "ethers";
import { useWriteContract } from "wagmi";
import { waitForTransactionReceipt } from "wagmi/actions";
import type { WriteContractData } from "wagmi/query";

const useModPermsContracts = () => {
  const { isPendingTransaction, setIsPendingTransaction } = useUIStore(
    ({ isPendingTransaction, setIsPendingTransaction }) => ({
      isPendingTransaction,
      setIsPendingTransaction,
    }),
  );

  const isSocialAccountBeingCreated = useUIStore(
    (state) => state.isSocialAccountBeingCreated,
  );
  const setSocialAccountStatus = useUIStore(
    (state) => state.setIsSocialAccountBeingCreated,
  );

  const { writeContractAsync } = useWriteContract({
    mutation: {
      onSuccess: async (hash) => {
        setIsPendingTransaction(true);
        try {
          const tx = await waitForTransactionReceipt(config, {
            hash: hash,
          });
          if (tx.status === "success") {
            if (isSocialAccountBeingCreated) {
              setTimeout(() => {
                setSocialAccountStatus(false);
                setIsPendingTransaction(false);
              }, 3000);
            } else {
              setIsPendingTransaction(false);
            }
          }
        } catch (error) {
          console.error(error);
          setIsPendingTransaction(false);
        }
      },
    },
  });

  //? Removes a single delegate
  const removeDelegationCall = async (delegate: RemoveSingleDelegateType) => {
    return await writeContractAsync({
      abi: BLESSNET_MOD_PERMS_ABI,
      address: BLESSNET_MOD_PERMS_ADDRESS,
      functionName: "removeDelegations",
      args: [[delegate as any]], // TODO abi shouldn't be never?
    });
  };

  //? Removes all delegations
  const removeAllDelegationsCall = async (
    walletAddress: HexString,
  ): Promise<WriteContractData> => {
    return await writeContractAsync({
      abi: BLESSNET_MOD_PERMS_ABI,
      address: BLESSNET_MOD_PERMS_ADDRESS,
      functionName: "removeAllForVaults",
      args: [[walletAddress]],
    });
  };

  //? Removes all recieved delegations
  const removeAllReceivedDelegationsCall = async (
    walletAddress: HexString,
  ): Promise<WriteContractData> => {
    return await writeContractAsync({
      abi: BLESSNET_MOD_PERMS_ABI,
      address: BLESSNET_MOD_PERMS_ADDRESS,
      functionName: "removeAllForDelegates",
      args: [[walletAddress]],
    });
  };

  //? Modifies (add or remove) permission configurations
  const modifyPermissionsCall = async (
    permissionModifications: ModifiedPermissionsRequest[],
  ): Promise<WriteContractData> => {
    return await writeContractAsync({
      abi: BLESSNET_MOD_PERMS_ABI,
      address: BLESSNET_MOD_PERMS_ADDRESS,
      functionName: "setPermissions",
      args: [permissionModifications as readonly any[]], // TODO abi shouldn't be never[]?
    });
  };

  //? Modifies (add or remove) permission configurations for social accounts
  const createSocialAccountWithPermissionsCall = async (
    calldataPayloads: SocialAccountCreationRequest[],
  ): Promise<WriteContractData> => {
    const multipliedFund = 0.001 * calldataPayloads.length;
    const parsedFund = ethers.parseEther(multipliedFund.toString());

    return await writeContractAsync({
      abi: BLESSNET_MOD_PERMS_ABI,
      address: BLESSNET_MOD_PERMS_ADDRESS,
      functionName: "callAndPermission",
      args: [calldataPayloads as readonly any[]], // TODO abi shouldn't be never[]?
      value: parsedFund,
    });
  };

  return {
    removeDelegationCall,
    removeAllDelegationsCall,
    removeAllReceivedDelegationsCall,
    modifyPermissionsCall,
    createSocialAccountWithPermissionsCall,
  };
};

export default useModPermsContracts;
